import React, { useRef } from 'react'
import {
  ChartDescription,
  ChartLegend,
  Container,
  ContainerAlert,
  ContentBlock,
  ContentLayout,
  Divisor,
  ChartArea,
  ChartFooterLabel,
  HeaderContentBlock,
  HeaderLayout,
  Label,
  LegendItem,
  SubTitleInformations,
  TitleInformation,
  Value
} from './styles'

import { BabyGrowthChart, BabyGrowthChartImperativeMethods } from './BabyGrowthChart'
import {
  BabyGrowthMeasurement,
  EBiologicalSex,
  HeadCircumferenceMeasurement,
  HeightMeasurement,
  IPatient,
  UnionTypeBiologicalSex,
  WeightMeasurement
} from '~/interfaces'
import {
  boyHeadCircumferencePercentiles,
  boyHeightPercentiles,
  boyWeightPercentiles,
  girlHeadCircumferencePercentiles,
  girlHeightPercentiles,
  girlWeightPercentiles
} from '~/constants'
import { getIntervalBetweenCurrentDateAnd, getMonthDiff, getTimestamp } from '~/utils'
import {
  useFetchBabyGrowthWeightMeasurements,
  useFetchBabyGrowthHeightMeasurements,
  useFetchBabyGrowthHeadCircumferenceMeasurements
} from '~/hooks'
import { SpinnerIconLoader } from '~/components/shared'
import { IconComponent } from '~/assets/icons/component'
import { message } from '~/services'

const Loading = (): JSX.Element => (
  <ContainerAlert>
    <SpinnerIconLoader iconComponent={<IconComponent name={'loading'} />} size={75} velocity={2} />
  </ContainerAlert>
)

const Error = ({ children }): JSX.Element => (
  <ContainerAlert>
    <span>{children}</span>
  </ContainerAlert>
)

interface HeaderProps {
  motherName: string
  babyName: string
  babyWeight: number | undefined
  babyBirthDate: string
}

const Header = ({ motherName, babyName, babyWeight, babyBirthDate }: HeaderProps): JSX.Element => {
  return (
    <HeaderLayout>
      <HeaderContentBlock>
        <Label>Bebê</Label>
        <TitleInformation>{babyName}</TitleInformation>
      </HeaderContentBlock>

      <HeaderContentBlock>
        <Label>Última pesagem</Label>
        <Value>{babyWeight ? `${babyWeight}kg` : 'Sem medição'}</Value>
      </HeaderContentBlock>

      <HeaderContentBlock>
        <Label>Idade</Label>
        <Value>{getIntervalBetweenCurrentDateAnd(new Date(babyBirthDate))}</Value>
      </HeaderContentBlock>

      <HeaderContentBlock>
        <Label>Mãe</Label>
        <Value>{motherName}</Value>
      </HeaderContentBlock>
    </HeaderLayout>
  )
}

interface ContentProps {
  biologicalSex: UnionTypeBiologicalSex
  weightMeasurements: WeightMeasurement[]
  heightMeasurements: HeightMeasurement[]
  headCircumferenceMeasurements: HeadCircumferenceMeasurement[]
  chartStartDate: string
}

const parseDataAsAnnotations = (data: BabyGrowthMeasurement[], baseDate: string) => {
  return data.map(measure => {
    return {
      y: measure.value,
      x: getMonthDiff(getTimestamp(baseDate), getTimestamp(measure.date))
    }
  })
}

const hasValidMeasurementValue = (value: number, maxValue: number): boolean => value <= maxValue

const Content = ({
  biologicalSex,
  weightMeasurements,
  heightMeasurements,
  headCircumferenceMeasurements,
  chartStartDate
}: ContentProps): JSX.Element => {
  const babyWeightChartImperativeMethods = useRef<BabyGrowthChartImperativeMethods>(null)
  const babyHeightChartImperativeMethods = useRef<BabyGrowthChartImperativeMethods>(null)
  const babyHeadCircumferenceChartImperativeMethods = useRef<BabyGrowthChartImperativeMethods>(null)

  const biologicalSexLabel = biologicalSex === EBiologicalSex.FEMALE ? 'Menina' : 'Menino'
  const weightPercentiles = biologicalSex === EBiologicalSex.FEMALE ? girlWeightPercentiles : boyWeightPercentiles
  const heightPercentiles = biologicalSex === EBiologicalSex.FEMALE ? girlHeightPercentiles : boyHeightPercentiles
  const headCircumferencePercentiles =
    biologicalSex === EBiologicalSex.FEMALE ? girlHeadCircumferencePercentiles : boyHeadCircumferencePercentiles

  const MAX_WEIGHT = 30
  const weightChartData = weightMeasurements.filter(measurement =>
    hasValidMeasurementValue(measurement.value, MAX_WEIGHT)
  )

  const MAX_HEIGHT = 160
  const heightChartData = heightMeasurements.filter(measurement =>
    hasValidMeasurementValue(measurement.value, MAX_HEIGHT)
  )

  const MAX_HEAD_CIRCUMFERENCE = 160
  const headCircumferenceChartData = headCircumferenceMeasurements.filter(measurement =>
    hasValidMeasurementValue(measurement.value, MAX_HEAD_CIRCUMFERENCE)
  )

  return (
    <ContentLayout>
      <ContentBlock>
        <TitleInformation>Peso para idade</TitleInformation>

        <SubTitleInformations>
          <ChartDescription>0 a 2 Anos - {biologicalSexLabel} (percentis)</ChartDescription>
          <ChartLegend onClick={() => babyWeightChartImperativeMethods.current?.handleToggleAnnotations()}>
            <LegendItem />
            <Label>Medições</Label>
          </ChartLegend>
        </SubTitleInformations>
        <ChartArea>
          {weightChartData.length > 0 ? (
            <BabyGrowthChart
              ref={babyWeightChartImperativeMethods}
              percentiles={weightPercentiles}
              axisLabels={{ x: 'mês', y: 'kg' }}
              annotations={parseDataAsAnnotations(weightChartData, chartStartDate)}
            />
          ) : (
            <Error>{message('maternalInfant.baby_growth_empty_weight_chart')}</Error>
          )}
        </ChartArea>
        <ChartFooterLabel>* Padrões de crescimento infantil da OMS</ChartFooterLabel>
      </ContentBlock>

      <ContentBlock>
        <TitleInformation>Comprimento para idade</TitleInformation>

        <SubTitleInformations>
          <ChartDescription>0 a 2 Anos - {biologicalSexLabel} (percentis)</ChartDescription>
          <ChartLegend onClick={() => babyHeightChartImperativeMethods.current?.handleToggleAnnotations()}>
            <LegendItem />
            <Label>Medições</Label>
          </ChartLegend>
        </SubTitleInformations>
        <ChartArea>
          {heightChartData.length > 0 ? (
            <BabyGrowthChart
              ref={babyHeightChartImperativeMethods}
              percentiles={heightPercentiles}
              axisLabels={{ x: 'mês', y: 'cm' }}
              annotations={parseDataAsAnnotations(heightChartData, chartStartDate)}
            />
          ) : (
            <Error>{message('maternalInfant.baby_growth_empty_height_chart')}</Error>
          )}
        </ChartArea>
        <ChartFooterLabel>* Padrões de crescimento infantil da OMS</ChartFooterLabel>
      </ContentBlock>

      <ContentBlock>
        <TitleInformation>Perímetro Cefálico para idade</TitleInformation>

        <SubTitleInformations>
          <ChartDescription>0 a 2 Anos - {biologicalSexLabel}</ChartDescription>
          <ChartLegend onClick={() => babyHeadCircumferenceChartImperativeMethods.current?.handleToggleAnnotations()}>
            <LegendItem />
            <Label>Medições</Label>
          </ChartLegend>
        </SubTitleInformations>
        <ChartArea>
          {headCircumferenceChartData.length > 0 ? (
            <BabyGrowthChart
              ref={babyHeadCircumferenceChartImperativeMethods}
              percentiles={headCircumferencePercentiles}
              axisLabels={{ x: 'mês', y: 'cm' }}
              annotations={parseDataAsAnnotations(headCircumferenceChartData, chartStartDate)}
            />
          ) : (
            <Error>{message('maternalInfant.baby_growth_empty_head_circumference_chart')}</Error>
          )}
        </ChartArea>
        <ChartFooterLabel>* Padrões da OMS de 2006</ChartFooterLabel>
      </ContentBlock>
    </ContentLayout>
  )
}

interface IBabyGrowthProps {
  motherPatient: IPatient
  childPatient: IPatient
}

export const BabyGrowth = ({ motherPatient, childPatient }: IBabyGrowthProps): JSX.Element => {
  const {
    data: weightMeasurements,
    isLoading: isLoadingWeightMeasurements,
    error: errorWeightMeasurements
  } = useFetchBabyGrowthWeightMeasurements({ pathParam: { patientId: childPatient.id } })

  const {
    data: heightMeasurements,
    isLoading: isLoadingHeightMeasurements,
    error: errorHeightMeasurements
  } = useFetchBabyGrowthHeightMeasurements({ pathParam: { patientId: childPatient.id } })

  const {
    data: headCircumferenceMeasurements,
    isLoading: isLoadingHeadcircumferenceMeasurements,
    error: errorHeadcircumferenceMeasurements
  } = useFetchBabyGrowthHeadCircumferenceMeasurements({ pathParam: { patientId: childPatient.id } })

  if (isLoadingWeightMeasurements || isLoadingHeightMeasurements || isLoadingHeadcircumferenceMeasurements) {
    return <Loading />
  }

  if (errorWeightMeasurements) return <Error>{errorWeightMeasurements.message}</Error>
  if (errorHeightMeasurements) return <Error>{errorHeightMeasurements.message}</Error>
  if (errorHeadcircumferenceMeasurements) return <Error>{errorHeadcircumferenceMeasurements.message}</Error>

  const lastBabyWeightMeasurement = [...weightMeasurements].pop()

  return (
    <Container>
      <Header
        motherName={motherPatient.name}
        babyName={childPatient.name}
        babyWeight={lastBabyWeightMeasurement?.value}
        babyBirthDate={childPatient.birthDate}
      />

      <Divisor />

      <Content
        biologicalSex={childPatient.biologicalSex}
        chartStartDate={childPatient.birthDate}
        weightMeasurements={weightMeasurements || []}
        heightMeasurements={heightMeasurements || []}
        headCircumferenceMeasurements={headCircumferenceMeasurements || []}
      />
    </Container>
  )
}
